import { useState, useCallback } from "react";
import { useBrandIntegrityNotifications } from "./useBrandIntegrityNotifications";

export interface RunningTest {
  testRunId: string;
  agentName: string;
  phase: "generating" | "executing";
  categoriesComplete: number;
  totalCategories: number;
  totalTests: number;
  completedTests: number;
  status: "running" | "completed" | "failed";
}

export const useBrandIntegrityTestTracking = () => {
  const [runningTests, setRunningTests] = useState<RunningTest[]>([]);

  const addRunningTest = useCallback((test: { testRunId: string; agentName: string; totalTests: number }) => {
    const id = String(test.testRunId);
    // full_audit starts with totalTests: 0 (unknown until generation completes)
    const phase: RunningTest["phase"] = test.totalTests > 0 ? "executing" : "generating";
    setRunningTests((prev) => {
      if (prev.some((t) => t.testRunId === id)) return prev;
      return [...prev, { ...test, testRunId: id, phase, categoriesComplete: 0, totalCategories: 0, completedTests: 0, status: "running" }];
    });
  }, []);

  const handleProgress = useCallback((testRunId: string, progress: number, total: number) => {
    const id = String(testRunId);
    setRunningTests((prev) => {
      if (!prev.some((t) => t.testRunId === id)) return prev;
      return prev.map((t) =>
        t.testRunId === id ? { ...t, phase: "executing", completedTests: progress, totalTests: total } : t,
      );
    });
  }, []);

  const handleCompleted = useCallback((testRunId: string) => {
    const id = String(testRunId);
    setRunningTests((prev) => prev.filter((t) => t.testRunId !== id));
  }, []);

  const handleStarted = useCallback((testRunId: string, agentName: string, runType?: string) => {
    const id = String(testRunId);
    const phase = runType === "full_audit" ? "generating" : "executing";
    setRunningTests((prev) => {
      if (prev.some((t) => t.testRunId === id)) return prev;
      return [...prev, { testRunId: id, agentName, phase, categoriesComplete: 0, totalCategories: 0, totalTests: 0, completedTests: 0, status: "running" }];
    });
  }, []);

  useBrandIntegrityNotifications(handleProgress, handleCompleted, handleStarted, {
    onGenerationStarted: ({ testRunId, totalCategories }) => {
      setRunningTests((prev) =>
        prev.map((t) =>
          t.testRunId === String(testRunId) ? { ...t, phase: "generating", totalCategories } : t,
        ),
      );
    },
    onGenerationProgress: ({ testRunId }) => {
      setRunningTests((prev) =>
        prev.map((t) =>
          t.testRunId === String(testRunId) ? { ...t, categoriesComplete: t.categoriesComplete + 1 } : t,
        ),
      );
    },
    onGenerationComplete: ({ testRunId, scenarioCount }) => {
      setRunningTests((prev) =>
        prev.map((t) =>
          t.testRunId === String(testRunId) ? { ...t, phase: "executing", totalTests: scenarioCount } : t,
        ),
      );
    },
  });

  const isAnyTestRunning = runningTests.some((t) => t.status === "running");

  return { runningTests, addRunningTest, isAnyTestRunning };
};
